// Assembling an invoice report in the browser: fetch each source packet, cut
// out each invoice's pages, draw the cover listing, save the file.
//
// WHY HERE AND NOT ON THE SERVER (`docs/decisions.md`). The API Lambda has 29
// seconds and a real report will not fit; a background worker would fit but
// costs a new queue, a new function, a polling UI, and a PHI-bearing PDF stored
// at rest with a retention question attached. Doing it here stores no new PHI,
// adds no infrastructure, and reuses `GET /uploads/{upload_id}/pdf-url`, which
// applies the document-access check itself.
//
// ORDER OF OPERATIONS, and it is the whole design: FETCH, then LAY OUT, then
// DRAW. The contents listing has to reflect what actually reached the file, not
// what the page spans promised — a packet that will not parse, or a span that
// overruns its packet, must show as "no source pages" rather than as a page
// number pointing at someone else's invoice.
//
// A PACKET THAT CANNOT BE FETCHED STOPS THE REPORT (owner, 2026-10-13 — D-353).
// Every invoice a report lists is one the caller may open, documents included,
// so a fetch that fails is a fault, never a permission outcome. Listing those
// invoices without pages once hid a CORS fault that refused every document.
//
// ONE PARSE PER PACKET, not per invoice. A packet commonly holds several
// invoices, and `PDFDocument.load` on a scanned multi-megabyte upload is the
// expensive step — the same reason `backend/src/platform/pdf.ts` cuts every
// range from one parse.

import { rgb, type PDFDocument, type PDFFont, type PDFPage } from 'pdf-lib'
import { BRAND_BLUE, BRAND_INK, BRAND_PATHS, BRAND_VIEWBOX } from './brand'
import { flipSvgPathY } from './svgPathFlip'

/**
 * `pdf-lib` is loaded ON DEMAND, not imported at module scope.
 *
 * It is ~350 kB gzipped and only the Reporting sub-tab ever needs it, so a
 * static import puts it in the main chunk and every user pays for it on first
 * load. The dynamic import gives Vite a split point instead: the library
 * arrives when someone actually clicks Submit.
 */
async function pdfLib() {
  return await import('pdf-lib')
}
import { REPORT_MAX_BYTES, type ReportCriteria, type ReportInvoiceRow } from '../api/types'
import { downloadUrl } from './download'
import {
  BRAND_BLOCK_HEIGHT,
  COLUMN_HEADER_HEIGHT,
  CRITERIA_PANEL_PADDING,
  FOOTER_HEIGHT,
  MARGIN,
  PAGE_HEIGHT,
  PAGE_WIDTH,
  ROW_FONT_SIZE,
  ROW_LINE_HEIGHT,
  ROW_PADDING,
  TOTALS_HEIGHT,
  layOutReport,
  omittedCompanyFlagLines,
  type CountedRow,
  type LaidOutRow,
} from './reportLayout'

/** How many packets to fetch at once. Four keeps a large report moving without
    opening a hundred parallel connections to S3 from one tab. */
const FETCH_CONCURRENCY = 4

/** How tall the brand mark is drawn. `BRAND_BLOCK_HEIGHT` is the lockup's own
    height in the layout arithmetic; they are the same number for a reason. */
const BRAND_SIZE = BRAND_BLOCK_HEIGHT

/** The criteria panel's tint. Its padding is `CRITERIA_PANEL_PADDING`, which
    lives with the layout arithmetic so the two cannot disagree. */
const PANEL_FILL = '#eaf3fb'

/** The omitted-invoices warning panel (D-354), in the app's warning colours:
    the flagged-field border (`colorPaletteMarigoldBorder2`, `lib/theme.ts`)
    and the warning box's tint and text (`WarningBanner.tsx`'s Yellow tokens). */
const WARNING_FILL = '#fffef5'
const WARNING_BORDER = '#eaa300'
const WARNING_INK = '#817400'

/** The column header band. */
const HEADER_FILL = '#f0f2f5'

/** The hairline between invoices (owner, 2026-09-10). Light enough to separate
    without ruling the page into a grid. */
const ROW_DIVIDER = '#dfe3e8'

/** Secondary text — the run lines, the footer, the no-pages note. */
const META_INK = '#5c6470'

/** `#rrggbb` as a pdf-lib colour. The brand's own hexes live in `lib/brand.ts`. */
function hexColor(hex: string) {
  const value = hex.replace('#', '')
  const at = (i: number) => parseInt(value.slice(i, i + 2), 16) / 255
  return rgb(at(0), at(2), at(4))
}

/**
 * A rounded rectangle, authored TOP-DOWN so pdf-lib's own `scale(1, -1)` lands
 * it with `(x, y)` at its top-left. Unlike the brand paths this needs no flip:
 * it is written in the convention pdf-lib expects, and only the brand artwork
 * is back-to-front (see `lib/svgPathFlip.ts`).
 *
 * `drawRectangle` has no corner radius, and circles-plus-rectangles cannot
 * carry one continuous border, so the panel is a single path.
 */
function roundedRectPath(width: number, height: number, radius: number): string {
  const r = Math.min(radius, width / 2, height / 2)
  return [
    `M${r} 0`,
    `L${width - r} 0`,
    `C${width - r / 2} 0 ${width} ${r / 2} ${width} ${r}`,
    `L${width} ${height - r}`,
    `C${width} ${height - r / 2} ${width - r / 2} ${height} ${width - r} ${height}`,
    `L${r} ${height}`,
    `C${r / 2} ${height} 0 ${height - r / 2} 0 ${height - r}`,
    `L0 ${r}`,
    `C0 ${r / 2} ${r / 2} 0 ${r} 0`,
    'Z',
  ].join(' ')
}

/**
 * `drawText` with letter spacing, which pdf-lib does not offer.
 *
 * One `drawText` per glyph. Fine for a sixteen-character wordmark drawn once per
 * report; it is not a general text renderer and nothing else should use it.
 */
function drawTracked(
  page: PDFPage,
  text: string,
  opts: { x: number; y: number; size: number; font: PDFFont; color: ReturnType<typeof rgb>; tracking: number },
): void {
  let x = opts.x
  for (const glyph of text) {
    page.drawText(glyph, { x, y: opts.y, size: opts.size, font: opts.font, color: opts.color })
    x += opts.font.widthOfTextAtSize(glyph, opts.size) + opts.tracking
  }
}

/** The ordinal suffix for a day of the month — `10th`, `21st`, `13th`. */
function ordinal(day: number): string {
  if (day % 100 >= 11 && day % 100 <= 13) return 'th'
  if (day % 10 === 1) return 'st'
  if (day % 10 === 2) return 'nd'
  if (day % 10 === 3) return 'rd'
  return 'th'
}

/**
 * `September 10th, 2026 at 2:02 PM (Eastern)` (owner, 2026-09-10) — the phrasing
 * that reads as a sentence beside "Run by", where a bare timestamp did not.
 */
export function runOnPhrase(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(at)
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? ''
  const day = Number(get('day'))
  return (
    `${get('month')} ${day}${ordinal(day)}, ${get('year')} ` +
    `at ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()} (Eastern)`
  )
}


/** Raised when assembly cannot finish. The message is shown to the user. */
export class ReportAssemblyError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ReportAssemblyError'
  }
}

export interface AssembleProgress {
  /** `fetching` while packets are coming down, `assembling` while drawing. */
  phase: 'fetching' | 'assembling'
  done: number
  total: number
}

interface AssembleInput {
  invoices: readonly ReportInvoiceRow[]
  /** The NORMALIZED criteria the server recorded, already rendered to sentences
      by `describeCriteria` — so the page and the audit row read alike. */
  criteriaLines: readonly string[]
  /** Who ran it and when, for the cover's header. */
  requestedBy: string
  requestedAt: string
  /**
   * Set only when a RECORDED report is being downloaded again (owner,
   * 2026-09-14). The cover then carries two lines: the original run, then this.
   *
   * The distinction is worth a line of the page because the values below it are
   * today's, not the original run's — only the SET of invoices is pinned. A
   * reader comparing two copies of "the same" report needs to know which.
   */
  downloadedAt?: string

  /**
   * Fetches one upload's PDF bytes. Injected rather than imported so the
   * assembly path is testable without a network: production passes a function
   * that calls `getUploadPdfUrl` and then `fetch`.
   *
   * Throws when the packet cannot be fetched. That is never a permission
   * outcome: a report lists only invoices whose documents the caller may open
   * (D-353), so a refusal here is a fault and stops the report.
   */
  fetchUpload: (uploadId: string) => Promise<Uint8Array>
  /**
   * How many invoices the report left out because their company is not settled
   * (`CreateReportResponse.omitted_company_flag_count`, D-354). Drawn as a
   * warning under Criteria when above zero. Absent on a re-download: the set is
   * pinned, and the count was true only when the report ran.
   */
  omittedCompanyFlagCount?: number
  onProgress?: (progress: AssembleProgress) => void
}

interface AssembledReport {
  bytes: Uint8Array
  fileName: string
  totalPages: number
  /** Invoices listed without a page number, and why the count is worth showing. */
  missingPageCount: number
}

/** Eastern-time stamp for the file name, `:` swapped for `.` because a colon is
    illegal in a file name — the same convention the upload naming uses. */
function fileNameStamp(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at)
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '00'
  // `hour` can come back as "24" for midnight in some runtimes.
  const hour = get('hour') === '24' ? '00' : get('hour')
  return `${get('year')}-${get('month')}-${get('day')} ${hour}.${get('minute')}`
}


/** Run `work` over `items` with at most `limit` in flight, preserving order. */
async function mapLimited<T, R>(
  items: readonly T[],
  limit: number,
  work: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const index = next++
      if (index >= items.length) return
      results[index] = await work(items[index]!, index)
    }
  })
  await Promise.all(runners)
  return results
}

/** A page span that is present, positive and the right way round. */
function hasSpan(row: ReportInvoiceRow): boolean {
  const { upload_page_start: start, upload_page_end: end } = row
  return (
    row.upload_id !== null &&
    start !== null &&
    end !== null &&
    start >= 1 &&
    end >= start
  )
}

/** `$1,234.56` from a decimal string, or an em dash when there is no amount. */
function money(value: string | null): string {
  if (value === null || value.trim() === '') return '—'
  const n = Number(value)
  if (!Number.isFinite(n)) return value
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Truncate to fit `width`, with an ellipsis. A contents listing must not
    reflow: a wrapped cell would move every page number below it. */
function fit(text: string, font: PDFFont, size: number, width: number): string {
  if (font.widthOfTextAtSize(text, size) <= width) return text
  let kept = text
  while (kept.length > 1 && font.widthOfTextAtSize(`${kept}…`, size) > width) {
    kept = kept.slice(0, -1)
  }
  return `${kept}…`
}

// ---- The cover's columns --------------------------------------------------
//
// Page first, so the listing reads as a table of contents; the two money
// columns last and right-aligned. Widths sum to the usable width (612 - 72).
//
// STATUS IS GONE, replaced by Subtotal (owner, 2026-09-10). UPLOADED DATE IS
// GONE TOO (owner, 2026-09-11) — its 60 points went to the columns that were
// being truncated, Vendor and Company most of all. Invoice Date KEEPS ITS LABEL
// even now that it is the only date on the page: the Uploaded Date filter is
// still on the form, so a bare "Date" would leave a reader guessing which of the
// two they are looking at.

interface Column {
  label: string
  width: number
  align?: 'right'
}

const COLUMNS: readonly Column[] = [
  { label: 'Page', width: 28 },
  { label: 'Invoice Date', width: 56 },
  { label: 'Vendor', width: 116 },
  { label: 'Company', width: 108 },
  { label: 'Invoice #', width: 62 },
  { label: 'Code', width: 74 },
  { label: 'Subtotal', width: 48, align: 'right' },
  { label: 'Total', width: 48, align: 'right' },
]

/** `yyyy-MM-dd` from a stored date or timestamp, or an em dash. */
function dateCell(value: string | null): string {
  if (value === null || value.trim() === '') return '—'
  return value.slice(0, 10)
}

function cellsFor(entry: LaidOutRow): string[] {
  const { row, firstPage } = entry
  const codes = row.code_subtotals
  return [
    firstPage === null ? '—' : String(firstPage),
    dateCell(row.invoice_date),
    row.vendor_name ?? '—',
    row.company_name ?? '—',
    row.external_invoice_id ?? '—',
    // ONE LINE PER CODE, and the Subtotal column's lines are the same length by
    // construction, so a code and its amount are always on the same line.
    codes.length > 0 ? codes.map((c) => c.code).join('\n') : '—',
    codes.length > 0 ? codes.map((c) => money(c.subtotal)).join('\n') : '—',
    money(row.total),
  ]
}

/**
 * Assemble the report.
 *
 * An invoice with no page span, or whose packet will not parse, is listed with
 * no page number and counted in `missingPageCount`. It throws
 * `ReportAssemblyError` when any packet cannot be fetched (D-353), and when the
 * assembled size passes `REPORT_MAX_BYTES`, because a tab that exhausts its own
 * memory takes the user's unsaved work with it.
 */
export async function assembleReport(input: AssembleInput): Promise<AssembledReport> {
  const { invoices, criteriaLines, fetchUpload, onProgress } = input
  const { PDFDocument, StandardFonts } = await pdfLib()

  // --- fetch: one parse per PACKET, in the order the report lists them -----
  const uploadIds = [
    ...new Set(invoices.filter(hasSpan).map((row) => row.upload_id as string)),
  ]
  let fetched = 0
  onProgress?.({ phase: 'fetching', done: 0, total: uploadIds.length })

  const packets = new Map<string, PDFDocument>()
  let assembledBytes = 0
  let unfetched = 0

  await mapLimited(uploadIds, FETCH_CONCURRENCY, async (uploadId) => {
    let bytes: Uint8Array | null = null
    try {
      bytes = await fetchUpload(uploadId)
    } catch {
      // Counted, and the report stops once every fetch has settled (D-353).
      unfetched += 1
    }
    if (bytes !== null) {
      assembledBytes += bytes.byteLength
      if (assembledBytes > REPORT_MAX_BYTES) {
        throw new ReportAssemblyError(
          `This report's source documents come to more than ${Math.round(
            REPORT_MAX_BYTES / (1024 * 1024),
          )} MB, which is more than a browser can assemble. Narrow the criteria and try again.`,
        )
      }
      try {
        packets.set(
          uploadId,
          // `ignoreEncryption`: some scanners set an owner password with no
          // user password, which permits reading — the same allowance the
          // server's splitter makes, and for the same reason. `updateMetadata`
          // is irrelevant on a source we only read.
          await PDFDocument.load(bytes, { ignoreEncryption: true }),
        )
      } catch {
        // A packet that arrived but will not parse is NOT a failed report: the
        // invoices in it are listed without page numbers. Anything else would
        // let one unreadable scan from 2024 refuse a whole month's report.
      }
    }
    fetched += 1
    onProgress?.({ phase: 'fetching', done: fetched, total: uploadIds.length })
  })

  if (unfetched > 0) {
    throw new ReportAssemblyError(
      uploadIds.length > 1 && unfetched === uploadIds.length
        ? `None of this report’s ${unfetched} source documents could be fetched, so nothing was downloaded. Try again, and if it happens again, please report it.`
        : unfetched === 1
        ? 'One of this report’s source documents could not be fetched, so nothing was downloaded. Try again, and if it happens again, please report it.'
        : `Of this report’s ${uploadIds.length} source documents, ${unfetched} could not be fetched, so nothing was downloaded. Try again, and if it happens again, please report it.`,
    )
  }

  // --- count what actually arrived, then lay out ---------------------------
  const counted: CountedRow[] = invoices.map((row) => {
    if (!hasSpan(row)) return { row, pageCount: 0 }
    const packet = packets.get(row.upload_id as string)
    if (packet === undefined) return { row, pageCount: 0 }
    const start = row.upload_page_start as number
    const end = row.upload_page_end as number
    // A span that overruns its packet is a data problem, not a reason to point
    // at the wrong pages: clamp to what the packet actually has, and if that
    // leaves nothing, the invoice is listed without pages.
    const last = Math.min(end, packet.getPageCount())
    return { row, pageCount: last >= start ? last - start + 1 : 0 }
  })

  const warningLines = omittedCompanyFlagLines(input.omittedCompanyFlagCount ?? 0)
  const layout = layOutReport(
    counted,
    criteriaLines.length,
    input.downloadedAt === undefined ? 1 : 2,
    warningLines.length,
  )
  onProgress?.({ phase: 'assembling', done: 0, total: layout.totalPages })

  // --- draw ---------------------------------------------------------------
  const out = await PDFDocument.create()
  out.setTitle('Invoice Report')
  const body = await out.embedFont(StandardFonts.Helvetica)
  const bold = await out.embedFont(StandardFonts.HelveticaBold)

  const coverPages: PDFPage[] = []
  for (let i = 0; i < layout.coverPageCount; i++) {
    coverPages.push(out.addPage([PAGE_WIDTH, PAGE_HEIGHT]))
  }

  drawCover({
    pages: coverPages,
    layout,
    criteriaLines,
    warningLines,
    requestedBy: input.requestedBy,
    requestedAt: input.requestedAt,
    downloadedAt: input.downloadedAt,
    body,
    bold,
  })

  // The source pages, in the report's own order.
  let appended = 0
  for (const entry of layout.rows) {
    if (entry.pageCount <= 0) continue
    const packet = packets.get(entry.row.upload_id as string)
    if (packet === undefined) continue
    const start = entry.row.upload_page_start as number
    const indices = Array.from({ length: entry.pageCount }, (_, i) => start - 1 + i)
    const copied = await out.copyPages(packet, indices)
    for (const page of copied) out.addPage(page)
    appended += entry.pageCount
    onProgress?.({
      phase: 'assembling',
      done: layout.coverPageCount + appended,
      total: layout.totalPages,
    })
  }

  const bytes = await out.save()
  if (bytes.byteLength > REPORT_MAX_BYTES) {
    throw new ReportAssemblyError(
      `The assembled report is larger than ${Math.round(
        REPORT_MAX_BYTES / (1024 * 1024),
      )} MB. Narrow the criteria and try again.`,
    )
  }

  return {
    bytes,
    fileName: `Invoice Report - ${fileNameStamp(new Date(input.requestedAt))}.pdf`,
    totalPages: layout.totalPages,
    missingPageCount: layout.missingPageCount,
  }
}

function drawCover(args: {
  pages: readonly PDFPage[]
  layout: ReturnType<typeof layOutReport>
  criteriaLines: readonly string[]
  warningLines: readonly string[]
  requestedBy: string
  requestedAt: string
  downloadedAt?: string
  body: PDFFont
  bold: PDFFont
}): void {
  const { pages, layout, criteriaLines, body, bold } = args
  const usableWidth = PAGE_WIDTH - MARGIN * 2

  let pageIndex = 0
  let page = pages[0]!
  let y = PAGE_HEIGHT - MARGIN

  // ---- the brand lockup and the title ------------------------------------
  //
  // The mark in its own colours, then the wordmark the app header carries, then
  // the document's own title under both. The paths are flipped into display
  // coordinates because pdf-lib takes path data and no transform — see
  // `lib/svgPathFlip.ts`.
  const markScale = BRAND_SIZE / BRAND_VIEWBOX
  for (const path of BRAND_PATHS) {
    page.drawSvgPath(flipSvgPathY(path.d, BRAND_VIEWBOX), {
      x: MARGIN,
      y,
      scale: markScale,
      color: hexColor(path.fill),
    })
  }

  const wordmarkX = MARGIN + BRAND_SIZE + 10
  // LETTER-SPACED A GLYPH AT A TIME, because pdf-lib's `drawText` has no
  // tracking option. The app header letter-spaces this wordmark, and matching it
  // is what makes the block read as the same lockup rather than as bold body
  // text.
  drawTracked(page, 'ACCOUNTS PAYABLE', {
    x: wordmarkX,
    y: y - 11,
    size: 9,
    font: bold,
    color: hexColor(BRAND_INK),
    tracking: 1.4,
  })
  page.drawText('Invoice Report', {
    x: wordmarkX,
    y: y - 30,
    size: 18,
    font: bold,
    color: hexColor(BRAND_BLUE),
  })
  y -= BRAND_SIZE + 6

  // ---- who and when, on ONE line (owner, 2026-09-10) ---------------------
  //
  // A SECOND line appears only on a re-download (owner, 2026-09-14). The first
  // line always names the ORIGINAL run, because that is what the criteria below
  // describe and what the invoice set was chosen by.
  page.drawText(`Run by ${args.requestedBy} on ${runOnPhrase(args.requestedAt)}`, {
    x: MARGIN,
    y: y - ROW_FONT_SIZE,
    size: ROW_FONT_SIZE,
    font: body,
    color: hexColor(META_INK),
  })
  y -= ROW_LINE_HEIGHT
  if (args.downloadedAt !== undefined) {
    page.drawText(`Downloaded again on ${runOnPhrase(args.downloadedAt)}`, {
      x: MARGIN,
      y: y - ROW_FONT_SIZE,
      size: ROW_FONT_SIZE,
      font: body,
      color: hexColor(META_INK),
    })
    y -= ROW_LINE_HEIGHT
  }
  y -= 10

  // ---- the criteria panel ------------------------------------------------
  //
  // A TINTED, ROUNDED PANEL rather than two more lines of body text (owner,
  // 2026-09-10): the criteria are what makes the report answerable — what a
  // reader has to understand before the listing means anything — and they read
  // as a footnote when they are set like one.
  //
  // The panel's own path is authored TOP-DOWN and therefore needs no flip: it
  // is written in the same convention pdf-lib expects. Only the brand paths
  // above are back-to-front, because they were authored bottom-up.
  const lines = criteriaLines.length > 0 ? criteriaLines : ['All invoices you can see']
  const panelHeight = CRITERIA_PANEL_PADDING * 2 + ROW_LINE_HEIGHT + lines.length * ROW_LINE_HEIGHT
  page.drawSvgPath(roundedRectPath(usableWidth, panelHeight, 6), {
    x: MARGIN,
    y,
    color: hexColor(PANEL_FILL),
    borderColor: hexColor(BRAND_BLUE),
    borderWidth: 0.75,
  })
  let panelY = y - CRITERIA_PANEL_PADDING
  page.drawText('Criteria', {
    x: MARGIN + CRITERIA_PANEL_PADDING,
    y: panelY - ROW_FONT_SIZE,
    size: ROW_FONT_SIZE,
    font: bold,
    color: hexColor(BRAND_BLUE),
  })
  panelY -= ROW_LINE_HEIGHT
  for (const line of lines) {
    page.drawText(fit(line, body, ROW_FONT_SIZE, usableWidth - CRITERIA_PANEL_PADDING * 2), {
      x: MARGIN + CRITERIA_PANEL_PADDING,
      y: panelY - ROW_FONT_SIZE,
      size: ROW_FONT_SIZE,
      font: body,
      color: hexColor(BRAND_INK),
    })
    panelY -= ROW_LINE_HEIGHT
  }
  y -= panelHeight + 12

  // ---- the omitted-invoices warning (owner, 2026-10-13 — D-354) ----------
  //
  // Built like the criteria panel, in the warning colours, so a reader sees
  // before the listing that it is short, and why. Its height is in
  // `firstPageHeaderHeight`, term for term.
  if (args.warningLines.length > 0) {
    const warningHeight =
      CRITERIA_PANEL_PADDING * 2 + ROW_LINE_HEIGHT + args.warningLines.length * ROW_LINE_HEIGHT
    page.drawSvgPath(roundedRectPath(usableWidth, warningHeight, 6), {
      x: MARGIN,
      y,
      color: hexColor(WARNING_FILL),
      borderColor: hexColor(WARNING_BORDER),
      borderWidth: 0.75,
    })
    let warningY = y - CRITERIA_PANEL_PADDING
    page.drawText('Omitted Invoices', {
      x: MARGIN + CRITERIA_PANEL_PADDING,
      y: warningY - ROW_FONT_SIZE,
      size: ROW_FONT_SIZE,
      font: bold,
      color: hexColor(WARNING_INK),
    })
    warningY -= ROW_LINE_HEIGHT
    for (const line of args.warningLines) {
      page.drawText(fit(line, body, ROW_FONT_SIZE, usableWidth - CRITERIA_PANEL_PADDING * 2), {
        x: MARGIN + CRITERIA_PANEL_PADDING,
        y: warningY - ROW_FONT_SIZE,
        size: ROW_FONT_SIZE,
        font: body,
        color: hexColor(WARNING_INK),
      })
      warningY -= ROW_LINE_HEIGHT
    }
    y -= warningHeight + 12
  }

  // ---- the column header -------------------------------------------------
  //
  // A SHADED BAND with the rule under it, and the text vertically centred
  // inside. The first version drew the labels and then a line two points below
  // the baseline, so descenders sat on the rule — which is what "your headers
  // are overlapping with the underline" was describing.
  const drawColumnHeader = (): void => {
    page.drawRectangle({
      x: MARGIN,
      y: y - COLUMN_HEADER_HEIGHT,
      width: usableWidth,
      height: COLUMN_HEADER_HEIGHT,
      color: hexColor(HEADER_FILL),
    })
    const textY = y - COLUMN_HEADER_HEIGHT + (COLUMN_HEADER_HEIGHT - ROW_FONT_SIZE) / 2 + 1
    let x = MARGIN
    for (const column of COLUMNS) {
      const label = fit(column.label, bold, ROW_FONT_SIZE, column.width - 6)
      const offset =
        column.align === 'right'
          ? column.width - 4 - bold.widthOfTextAtSize(label, ROW_FONT_SIZE)
          : 3
      page.drawText(label, {
        x: x + offset,
        y: textY,
        size: ROW_FONT_SIZE,
        font: bold,
        color: hexColor(BRAND_INK),
      })
      x += column.width
    }
    page.drawLine({
      start: { x: MARGIN, y: y - COLUMN_HEADER_HEIGHT },
      end: { x: PAGE_WIDTH - MARGIN, y: y - COLUMN_HEADER_HEIGHT },
      thickness: 0.75,
      color: hexColor(BRAND_BLUE),
    })
    y -= COLUMN_HEADER_HEIGHT + 3
  }
  drawColumnHeader()

  const floor = MARGIN + FOOTER_HEIGHT
  const nextPage = (): void => {
    pageIndex += 1
    page = pages[pageIndex]!
    y = PAGE_HEIGHT - MARGIN
    drawColumnHeader()
  }

  // ---- the rows ----------------------------------------------------------
  //
  // ONE LINE PER CODE in the Code and Subtotal columns (owner, 2026-09-10);
  // every other column is drawn once, on the row's first line. `cellsFor`
  // returns the per-code cells as newline-joined text so both columns stay in
  // step with each other and with `rowLineCount`.
  for (const entry of layout.rows) {
    if (y - entry.height < floor && pageIndex + 1 < pages.length) nextPage()
    const cells = cellsFor(entry)
    let x = MARGIN
    for (let c = 0; c < COLUMNS.length; c++) {
      const column = COLUMNS[c]!
      const cellLines = cells[c]!.split('\n')
      for (let l = 0; l < cellLines.length; l++) {
        const text = fit(cellLines[l]!, body, ROW_FONT_SIZE, column.width - 6)
        const offset =
          column.align === 'right'
            ? column.width - 4 - body.widthOfTextAtSize(text, ROW_FONT_SIZE)
            : 3
        page.drawText(text, {
          x: x + offset,
          y: y - ROW_PADDING - ROW_FONT_SIZE - l * ROW_LINE_HEIGHT,
          size: ROW_FONT_SIZE,
          font: body,
          color: hexColor(BRAND_INK),
        })
      }
      x += column.width
    }
    y -= entry.height

    // A THIN DIVIDER UNDER EACH INVOICE (owner, 2026-09-10), so a row spanning
    // several code lines reads as one invoice rather than as several. Not after
    // the last row: the grand total's own rule follows immediately, and two
    // lines a few points apart look like a mistake.
    if (entry !== layout.rows[layout.rows.length - 1]) {
      page.drawLine({
        start: { x: MARGIN, y: y + ROW_PADDING / 2 },
        end: { x: PAGE_WIDTH - MARGIN, y: y + ROW_PADDING / 2 },
        thickness: 0.4,
        color: hexColor(ROW_DIVIDER),
      })
    }
  }

  // ---- the grand total ---------------------------------------------------
  //
  // RIGHT-ALIGNED to the Total column's own right edge (owner, 2026-09-10), so
  // it reads as the sum of the column above it rather than as a sentence that
  // happens to end in a number.
  if (y - TOTALS_HEIGHT < floor && pageIndex + 1 < pages.length) nextPage()
  const grandTotal = layout.rows.reduce((sum, entry) => sum + Number(entry.row.total ?? 0), 0)
  const count = layout.rows.length
  page.drawLine({
    start: { x: MARGIN, y: y - 2 },
    end: { x: PAGE_WIDTH - MARGIN, y: y - 2 },
    thickness: 0.75,
    color: hexColor(BRAND_BLUE),
  })
  y -= ROW_LINE_HEIGHT + 2
  page.drawText(`${count} invoice${count === 1 ? '' : 's'}`, {
    x: MARGIN + 3,
    y: y - ROW_FONT_SIZE,
    size: ROW_FONT_SIZE,
    font: bold,
    color: hexColor(BRAND_INK),
  })
  const totalLabel = money(grandTotal.toFixed(2))
  const totalWidth = bold.widthOfTextAtSize(totalLabel, ROW_FONT_SIZE)
  page.drawText(totalLabel, {
    x: PAGE_WIDTH - MARGIN - 4 - totalWidth,
    y: y - ROW_FONT_SIZE,
    size: ROW_FONT_SIZE,
    font: bold,
    color: hexColor(BRAND_INK),
  })
  y -= ROW_LINE_HEIGHT

  if (layout.missingPageCount > 0) {
    const n = layout.missingPageCount
    page.drawText(
      `${n} invoice${n === 1 ? '' : 's'} listed without source pages — manually entered, or the source document could not be read.`,
      {
        x: MARGIN + 3,
        y: y - ROW_FONT_SIZE,
        size: ROW_FONT_SIZE,
        font: body,
        color: hexColor(META_INK),
      },
    )
  }

  // ---- the footer, on the COVER PAGES ONLY --------------------------------
  //
  // Nothing is drawn on a source page. The cover's numbers are absolute
  // positions in the assembled file, which any viewer displays; stamping a
  // locator onto a vendor's own document would alter evidence
  // (`docs/decisions.md`).
  for (let i = 0; i < pages.length; i++) {
    const label = `Page ${i + 1} of ${layout.totalPages}`
    const width = body.widthOfTextAtSize(label, ROW_FONT_SIZE)
    pages[i]!.drawText(label, {
      x: (PAGE_WIDTH - width) / 2,
      y: MARGIN,
      size: ROW_FONT_SIZE,
      font: body,
      color: hexColor(META_INK),
    })
  }
}

/** Save an assembled report to the user's disk. */
export function saveReport(report: AssembledReport): void {
  // A Blob URL rather than a data: URI — a 60 MB base64 string is a needless
  // copy of a report we already hold in memory.
  const blob = new Blob([report.bytes as unknown as BlobPart], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  try {
    downloadUrl(url, report.fileName)
  } finally {
    // Revoked on the next tick: the anchor click is synchronous, but the
    // browser reads the URL just after it.
    setTimeout(() => URL.revokeObjectURL(url), 10_000)
  }
}

export type { ReportCriteria }
